import { Icon } from '@amelio/ui-library';

import { CompetencyRow } from './CompetencyRow';

import type { EmployeeLevels } from './hooks/useProfileSelectorState';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

type CompetencyData = {
    id: string;
    name: Record<string, string>;
    description: Record<string, string>;
};

type GroupData = {
    id: string;
    name: Record<string, string>;
};

type CompetencyItem = {
    competency: CompetencyData;
    isPriority: boolean;
};

type CompetencyGroupAccordionProps = {
    group: GroupData;
    items: CompetencyItem[];
    isExpanded: boolean;
    removedIds: Set<string>;
    /** The competencies the employee already holds in their current profile. */
    heldIds?: ReadonlySet<string>;
    onToggleExpanded: () => void;
    /** Absent, the profile's competencies only read: none can be removed for the employee. */
    onToggleRemoved?: (competencyId: string) => void;
    /** Absent, no level is offered for the employee. */
    employeeLevels?: EmployeeLevels;
};

export const CompetencyGroupAccordion = ({
    group,
    items,
    isExpanded,
    removedIds,
    heldIds,
    onToggleExpanded,
    onToggleRemoved,
    employeeLevels,
}: CompetencyGroupAccordionProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    return (
        <div className={'rounded-lg border border-gray-100 bg-white/55'}>
            <button
                type={'button'}
                onClick={onToggleExpanded}
                className={
                    'flex w-full flex-col items-center gap-3 border-b border-gray-100 px-5 py-3 transition-colors sm:flex-row'
                }
            >
                <p className={'text-navy text-body font-semibold'}>
                    {getTextByLanguage(group.name, language)}
                </p>

                <span
                    className={
                        'rounded-full bg-[hsl(261,100%,96%)] px-3 py-1 text-xs font-medium text-[hsl(261,96%,50%)] sm:ml-auto'
                    }
                >
                    {t('competencies.competencyProfilesAdmin.selector.groupCount', {
                        count: items.length,
                    })}
                </span>
                <Icon
                    name={isExpanded ? 'arrow-arrow-up-1' : 'arrow-arrow-down'}
                    size={'sm'}
                />
            </button>

            {isExpanded && (
                <div className={'flex flex-col gap-3 p-6'}>
                    {items.map(({ competency, isPriority }) => (
                        <CompetencyRow
                            key={competency.id}
                            competency={competency}
                            isPriority={isPriority}
                            isRemoved={removedIds.has(competency.id)}
                            isHeld={heldIds?.has(competency.id)}
                            onToggleRemoved={
                                onToggleRemoved && (() => onToggleRemoved(competency.id))
                            }
                            employeeLevels={employeeLevels}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
